import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isEqual } from 'lodash';
import moment from 'moment';

const ConversationSnapshotFiltersContext = createContext();

export const useConversationSnapshotFiltersContext = () => {
  const context = useContext(ConversationSnapshotFiltersContext);
  if (!context) {
    throw new Error(
      'useConversationSnapshotFiltersContext must be used within a ConversationSnapshotFiltersProvider'
    );
  }
  return context;
};

// Default date range: Last 3 months
const getDefaultDateRange = () => {
  const start = moment()
    .subtract(3, 'months')
    .startOf('day')
    .format('YYYY-MM-DD');
  const end = moment().endOf('day').format('YYYY-MM-DD');
  return {
    start,
    end,
  };
};

const DEFAULT_FILTERS = {
  includeProductionHubs: true,
  includeSuperadmin: false,
  includeClientAdmin: true,
  includeNonAdminUsers: true,
  dateRange: getDefaultDateRange(),
};

export const ConversationSnapshotFiltersProvider = ({ children }) => {
  const [snapshotFilters, setSnapshotFilters] = useState(DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState(DEFAULT_FILTERS);

  const updateFilters = useCallback((key, newValue) => {
    setPendingFilters(prev => ({
      ...prev,
      [key]: newValue,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    if (isEqual(pendingFilters, DEFAULT_FILTERS)) {
      return;
    }
    setSnapshotFilters(DEFAULT_FILTERS);
    setPendingFilters(DEFAULT_FILTERS);
  }, [pendingFilters]);

  const applyFilters = useCallback(() => {
    setSnapshotFilters(pendingFilters);
  }, [pendingFilters]);

  const isDirty = useMemo(
    () => !isEqual(snapshotFilters, pendingFilters),
    [snapshotFilters, pendingFilters]
  );

  const enableClearAll = useMemo(
    () => !isEqual(pendingFilters, DEFAULT_FILTERS),
    [pendingFilters]
  );

  const value = {
    snapshotFilters,
    pendingFilters,
    isDirty,
    enableClearAll,
    setSnapshotFilters: updateFilters,
    resetFilters,
    applyFilters,
  };

  return (
    <ConversationSnapshotFiltersContext.Provider value={value}>
      {children}
    </ConversationSnapshotFiltersContext.Provider>
  );
};
